import { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { Package, ArrowLeft, CreditCard } from "lucide-react"
import { externalEntityService } from "../../services/externalEntityService"
import Alert from "../common/Alert"
import ExternalShipmentPaymentModal from "./external_shipment_payment/ExternalShipmentPaymentModal"
import {
  PageLayout,
  Button,
  DataTable,
  DetailCard,
  DetailRow,
  StatusBadge,
} from "../common/index.js"

const ViewExternalShipment = () => {
  // Route: /external-ml/:externalMLId/shipment/:shipmentId
  // shipmentId here is the external_shipment_id (string like "SI000001")
  const { externalMLId, shipmentId } = useParams()
  const navigate = useNavigate()
  const [shipment, setShipment] = useState(null)
  const [paymentHistory, setPaymentHistory] = useState([])
  const [loading, setLoading] = useState(true)
  const [showPaymentModal, setShowPaymentModal] = useState(false)

  useEffect(() => {
    fetchShipmentData()
  }, [shipmentId])

  const fetchShipmentData = async () => {
    try {
      setLoading(true)

      // GET /api/external-entities/shipments/<external_shipment_id>
      // Response: { success, shipment: {...}, payment_history: [...] }
      const response = await externalEntityService.getExternalShipment(shipmentId)
      const data = response.data

      setShipment(data.shipment || null)
      setPaymentHistory(Array.isArray(data.payment_history) ? data.payment_history : [])
    } catch (error) {
      console.error("Error fetching External Shipment:", error)
      Alert.error(error.response?.data?.message || "Failed to load shipment details")
      navigate(`/external-ml/view/${externalMLId}`)
    } finally {
      setLoading(false)
    }
  }

  const getPaymentModeVariant = (mode) => {
    const map = {
      to_pay: "warning",
      paid: "success",
      paid_balance: "info",
    }
    return map[(mode || "").toLowerCase()] || "default"
  }

  if (loading) {
    return (
      <PageLayout title="Shipment Details" icon={Package}>
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand mx-auto mb-4" />
            <p className="text-gray-600 font-semibold">Loading Shipment Details...</p>
          </div>
        </div>
      </PageLayout>
    )
  }

  if (!shipment) {
    return (
      <PageLayout title="Shipment Details" icon={Package}>
        <DetailCard>
          <p className="text-center text-gray-600 text-lg font-semibold">Shipment not found</p>
          <div className="mt-4 flex justify-center">
            <Button onClick={() => navigate(`/external-ml/view/${externalMLId}`)}>
              Back to External ML
            </Button>
          </div>
        </DetailCard>
      </PageLayout>
    )
  }

  const paymentColumns = [
    {
      key: "sr_no",
      label: "#",
      width: "50px",
      render: (_, index) => index + 1,
    },
    {
      key: "payment_amount",
      label: "Amount",
      cellClassName: "font-semibold text-green-600",
      render: (p) => `₹${(p.payment_amount || 0).toFixed(2)}`,
    },
    {
      key: "payment_mode",
      label: "Mode",
      render: (p) => p.payment_mode || "—",
    },
    {
      key: "previous_balance",
      label: "Prev Balance",
      render: (p) => `₹${(p.previous_balance || 0).toFixed(2)}`,
    },
    {
      key: "new_balance",
      label: "New Balance",
      cellClassName: "text-blue-600 font-semibold",
      render: (p) => `₹${(p.new_balance || 0).toFixed(2)}`,
    },
    {
      key: "payment_date",
      label: "Date",
      render: (p) =>
        p.payment_date
          ? new Date(p.payment_date).toLocaleDateString("en-IN")
          : "—",
    },
    {
      key: "recorded_by",
      label: "Recorded By",
      render: (p) => p.recorded_by_name || p.recorded_by_user_id || "—",
    },
    {
      key: "remarks",
      label: "Remarks",
      cellClassName: "break-all",
      render: (p) => p.remarks || "—",
    },
  ]

  return (
    <>
      <PageLayout
        title={`Shipment #${shipment.external_shipment_id}`}
        icon={Package}
        actions={
          <>
            <Button icon={CreditCard} onClick={() => setShowPaymentModal(true)}>
              Manage Payments
            </Button>
            <Button
              variant="secondary"
              icon={ArrowLeft}
              onClick={() => navigate(`/external-ml/view/${externalMLId}`)}
            >
              Back
            </Button>
          </>
        }
      >
        <p className="text-tertiary text-sm sm:text-base mb-6">
          LR No: {shipment.lr_no} · ML Sheet: {externalMLId}
        </p>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-sm shadow-md p-4 border-l-4 border-brand">
            <div className="text-gray-500 text-xs font-semibold uppercase mb-1">Total Amount</div>
            <div className="text-xl font-bold text-secondary">
              ₹{(shipment.total_amount || 0).toFixed(2)}
            </div>
          </div>
          <div className="bg-white rounded-sm shadow-md p-4 border-l-4 border-green-500">
            <div className="text-gray-500 text-xs font-semibold uppercase mb-1">Paid Amount</div>
            <div className="text-xl font-bold text-green-600">
              ₹{(shipment.paid_amount || 0).toFixed(2)}
            </div>
          </div>
          <div className="bg-white rounded-sm shadow-md p-4 border-l-4 border-red-400">
            <div className="text-gray-500 text-xs font-semibold uppercase mb-1">Balance</div>
            <div className="text-xl font-bold text-red-500">
              ₹{(shipment.balance_amount || 0).toFixed(2)}
            </div>
          </div>
          <div className="bg-white rounded-sm shadow-md p-4 border-l-4 border-brand">
            <div className="text-gray-500 text-xs font-semibold uppercase mb-1">Payment Mode</div>
            <StatusBadge
              status={shipment.payment_mode}
              variant={getPaymentModeVariant(shipment.payment_mode)}
              label={shipment.payment_mode || "—"}
              className="mt-1"
            />
          </div>
        </div>

        <DetailCard title="Shipment Details" className="mb-6">
          <DetailRow label="Shipment ID" value={shipment.external_shipment_id} />
          <DetailRow label="LR No" value={shipment.lr_no || "—"} />
          <DetailRow label="To Station" value={shipment.to_station || "—"} />
          <DetailRow label="Consignor" value={shipment.consignor || "—"} />
          <DetailRow label="Consignee" value={shipment.consignee || "—"} />
          <DetailRow label="Consignee Contact" value={shipment.consignee_no || "—"} />
          <DetailRow label="Quantity" value={shipment.quantity ?? "—"} />
          <DetailRow
            label="Service Charge (SC.Rs)"
            value={`₹${(shipment.sc_rs || 0).toFixed(2)}`}
          />
          <DetailRow
            label="Created At"
            value={
              shipment.created_at
                ? new Date(shipment.created_at).toLocaleString("en-IN")
                : "—"
            }
          />
        </DetailCard>

        <DetailCard
          title={`Payment History (${paymentHistory.length})`}
          className="mb-6"
        >
          <div className="flex justify-end mb-4 -mt-2">
            <Button
              size="sm"
              icon={CreditCard}
              onClick={() => setShowPaymentModal(true)}
            >
              Manage
            </Button>
          </div>
          <DataTable
            columns={paymentColumns}
            data={paymentHistory}
            emptyMessage="No payment records found"
            rowKey="id"
            className="shadow-none rounded-none"
          />
        </DetailCard>
      </PageLayout>

      {showPaymentModal && (
        <ExternalShipmentPaymentModal
          shipmentId={shipmentId}
          shipment={shipment}
          onClose={() => setShowPaymentModal(false)}
          onPaymentSaved={fetchShipmentData}
        />
      )}
    </>
  )
}

export default ViewExternalShipment
